// 26 ADM-002: every admin enrols a second factor (an authenticator app here;
// a passkey is preferred once Supabase offers it for MFA — VERIFY AT BUILD)
// before any page loads data. Requests carry aal2. SMS is never a factor.
import type { SupabaseClient } from '@supabase/supabase-js';

export type Gate = 'signed_out' | 'needs_enrol' | 'needs_code' | 'not_admin' | 'ok';

/** The admin_role claim the access-token hook puts in an admin's token, or null. */
export function adminRoleOf(accessToken: string | undefined): string | null {
  const part = accessToken?.split('.')[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { admin_role?: unknown };
    return typeof claims.admin_role === 'string' ? claims.admin_role : null;
  } catch {
    return null;
  }
}

export async function gate(sb: SupabaseClient): Promise<Gate> {
  const { data: s } = await sb.auth.getSession();
  if (!s.session) return 'signed_out';
  const { data, error } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return 'signed_out';
  if (data.currentLevel !== 'aal2') {
    const { data: f } = await sb.auth.mfa.listFactors();
    return f && f.totp.length > 0 ? 'needs_code' : 'needs_enrol';
  }
  // At aal2: the server marks the second factor (and, on an empty project, makes the first
  // owner). A new token then carries admin_role from the access-token hook.
  const { data: role } = await sb.schema('ops').rpc('admin_after_second_factor');
  if (typeof role !== 'string') return 'not_admin';
  if (adminRoleOf(s.session.access_token) === role) return 'ok';
  const { data: r } = await sb.auth.refreshSession();
  return adminRoleOf(r.session?.access_token) ? 'ok' : 'not_admin';
}

/** Starts enrolment. Clears any factor left half set up by an earlier try first. */
export async function enrolTotp(sb: SupabaseClient) {
  const { data: f } = await sb.auth.mfa.listFactors();
  for (const old of f?.all ?? [])
    if (old.factor_type === 'totp' && old.status !== 'verified')
      await sb.auth.mfa.unenroll({ factorId: old.id });
  const { data, error } = await sb.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: 'Fairway Life admin',
  });
  if (error) throw error;
  return data; // data.id is the new factor; data.totp.qr_code is an SVG data URL to scan
}

/**
 * Checks a 6-digit code. While enrolling, pass the new factor's id: it is not verified yet,
 * so it is not in the list of verified factors.
 */
export async function verifyCode(
  sb: SupabaseClient,
  code: string,
  factorId?: string,
): Promise<boolean> {
  let id = factorId;
  if (!id) {
    const { data: f } = await sb.auth.mfa.listFactors();
    id = f?.totp[0]?.id;
  }
  if (!id) return false;
  const { error } = await sb.auth.mfa.challengeAndVerify({ factorId: id, code });
  return !error;
}

/**
 * 26 ADM-006: removing an admin ends their sessions within 60 seconds. The server refuses a removed
 * admin at once (ops.admin_role() reads ops.admin_users on every request); the console asks it every
 * STILL_ADMIN_CHECK_MS and signs out when the answer is no.
 */
export const STILL_ADMIN_CHECK_MS = 30_000;

export async function stillAdmin(sb: SupabaseClient): Promise<boolean | 'unknown'> {
  const { data, error } = await sb.schema('ops').rpc('admin_role');
  if (error) return error.code === '42501' ? false : 'unknown'; // a network blip is not a removal
  return typeof data === 'string';
}
